import { useState, useEffect, useRef } from 'react'
import type { RefObject } from 'react'

type UseCompactCardOverlayParams = {
  isPanelPinned?: boolean
  scrollContainerRef?: RefObject<HTMLElement | null>
}

type UseCompactCardOverlayResult = {
  miniatureRef: RefObject<HTMLDivElement>
  isScrolledPast: boolean
}

/**
 * Detects when the post miniature has scrolled out of view so the caller
 * can fade/collapse it.
 *
 * Uses viewport-relative coordinates (getBoundingClientRect) to determine
 * when ~70% of the miniature has scrolled above the container top.
 */
export function useCompactCardOverlay({
  isPanelPinned,
  scrollContainerRef,
}: UseCompactCardOverlayParams): UseCompactCardOverlayResult {
  const miniatureRef = useRef<HTMLDivElement>(null)
  const isScrolledPastRef = useRef(false)
  const [isScrolledPast, setIsScrolledPast] = useState(false)

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current
    if (isPanelPinned || !scrollContainer) {
      if (isScrolledPastRef.current) {
        isScrolledPastRef.current = false
        setIsScrolledPast(false)
      }
      return
    }

    const handleScroll = (): void => {
      const miniature = miniatureRef.current
      if (!miniature) return

      const containerTop = scrollContainer.getBoundingClientRect().top
      const miniatureRect = miniature.getBoundingClientRect()
      const miniatureBottomInView = miniatureRect.bottom - containerTop

      // Show compact card once ~70% of the miniature has scrolled out.
      // For tall reels (~500px), a fixed 48px threshold would require
      // ~452px of scroll — often more than available. The proportional
      // threshold (30% of height) triggers when only a sliver remains.
      const threshold = Math.max(miniatureRect.height * 0.3, 48)

      if (!isScrolledPastRef.current) {
        if (miniatureBottomInView < threshold) {
          isScrolledPastRef.current = true
          setIsScrolledPast(true)
        }
      } else if (miniatureBottomInView >= threshold) {
        isScrolledPastRef.current = false
        setIsScrolledPast(false)
      }
    }

    handleScroll()

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return (): void => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      isScrolledPastRef.current = false
      setIsScrolledPast(false)
    }
  }, [scrollContainerRef, isPanelPinned])

  return { miniatureRef, isScrolledPast }
}
